import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BadgeProgress {
  slug: string;
  current: number;
  threshold: number;
  percentage: number;
}

export function useBadgeProgress(userId: string | null) {
  return useQuery({
    queryKey: ['badge-progress', userId],
    queryFn: async (): Promise<Record<string, BadgeProgress>> => {
      if (!userId) return {};

      const progress: Record<string, BadgeProgress> = {};

      const mkProgress = (slug: string, current: number, threshold: number) => {
        progress[slug] = {
          slug,
          current: Math.min(current, threshold),
          threshold,
          percentage: Math.min(100, (current / threshold) * 100),
        };
      };

      // Fetch all relevant data in parallel
      const [
        checkInsResult,
        reviewsResult,
        photosResult,
        sessionsResult,
      ] = await Promise.all([
        (supabase as any)
          .from('check_ins_visits')
          .select('cafe_id, checked_in_at, cafes!inner(neighborhood, longitude)')
          .eq('user_id', userId),
        supabase
          .from('reviews')
          .select('id, helpful_count')
          .eq('user_id', userId),
        supabase
          .from('cafe_photos')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_admin_upload', false)
          .eq('is_owner_upload', false),
        supabase
          .from('work_sessions')
          .select('total_focus_seconds')
          .eq('user_id', userId)
          .eq('is_completed', true),
      ]);

      const checkIns = checkInsResult.data || [];
      const reviews = reviewsResult.data || [];
      const photoCount = photosResult.count || 0;
      const sessions = sessionsResult.data || [];

      // === EXPLORATION ===
      // First Sip (unchanged)
      mkProgress('first_sip', checkIns.length, 1);

      // The Wanderer
      const uniqueNeighborhoods = new Set(
        checkIns.map((c: any) => c.cafes?.neighborhood?.toLowerCase()).filter(Boolean)
      ).size;
      mkProgress('neighborhood_hopper', uniqueNeighborhoods, 3);

      // Still Hours
      const hasDawn = checkIns.some(c => new Date(c.checked_in_at).getHours() < 8);
      mkProgress('dawn_patrol', hasDawn ? 1 : 0, 1);

      // Night Owl
      const hasNight = checkIns.some(c => new Date(c.checked_in_at).getHours() >= 19);
      mkProgress('night_owl', hasNight ? 1 : 0, 1);

      // === WORK ===
      // In The Zone
      mkProgress('in_the_zone', sessions.length, 1);

      // Flow State
      const has90Min = sessions.some(s => s.total_focus_seconds >= 5400);
      mkProgress('flow_state', has90Min ? 1 : 0, 1);

      // The Usual
      const cafeVisitCounts: Record<string, number> = {};
      checkIns.forEach(c => {
        cafeVisitCounts[c.cafe_id] = (cafeVisitCounts[c.cafe_id] || 0) + 1;
      });
      const maxSameCafe = Math.max(0, ...Object.values(cafeVisitCounts));
      mkProgress('the_regular', maxSameCafe, 5);

      // Monday Ritual
      const hasMonday = checkIns.some(c => {
        const d = new Date(c.checked_in_at);
        return d.getDay() === 1 && d.getHours() < 12;
      });
      mkProgress('monday_motivation', hasMonday ? 1 : 0, 1);

      // === THE COLLECTIVE ===
      // The Vibe Keeper
      mkProgress('storyteller', reviews.length, 1);

      // In Focus
      mkProgress('photographer', photoCount, 1);

      // Well Steeped
      const totalHelpful = reviews.reduce((sum, r) => sum + (r.helpful_count || 0), 0);
      mkProgress('trusted_voice', totalHelpful, 10);

      // The Arrival — can't easily compute client-side (would need to check if user was first at any cafe)
      // We'll show 0/1 and let the edge function handle awarding
      mkProgress('first_foot', 0, 1);

      // === THE HIDDEN MENU ===
      // Ten Deep
      const uniqueCafes = new Set(checkIns.map(c => c.cafe_id)).size;
      mkProgress('caffeine_tour', uniqueCafes, 5);

      // Deep Work (10 hours = 600 minutes)
      const totalMinutes = Math.floor(
        sessions.reduce((sum, s) => sum + (s.total_focus_seconds || 0), 0) / 60
      );
      mkProgress('laptop_warrior', totalMinutes, 600);

      return progress;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
}
